
import React from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Copy, RefreshCw, Sparkles, GraduationCap, Building } from 'lucide-react';
import { FAQData } from '@/components/faq-serp/types';
import FaqItem from './FaqItem';
import { toast } from 'sonner';

interface FaqTabsProps {
  faqs: FAQData;
  currentTab: string;
  setCurrentTab: (tab: string) => void;
  onGenerateNew: () => void;
}

const FaqTabs: React.FC<FaqTabsProps> = ({ 
  faqs, 
  currentTab, 
  setCurrentTab, 
  onGenerateNew 
}) => {
  const copyAllFaqs = () => {
    if (!faqs) return;
    
    // Format all FAQs for copying
    const allFaqs = [
      ...faqs.beginner,
      ...faqs.intermediate,
      ...faqs.expert,
      ...faqs.company
    ];
    
    const faqText = allFaqs.map(faq => {
      return `Q: ${faq.question}\n\nA: ${faq.answer.intro}\n${faq.answer.details.join('\n')}\n${faq.answer.conclusion}`;
    }).join('\n\n---\n\n');
    
    navigator.clipboard.writeText(faqText);
    toast.success("All FAQs copied to clipboard");
  };

  return (
    <div className="space-y-6 mt-4">
      <Tabs value={currentTab} onValueChange={setCurrentTab} className="space-y-6">
        <TabsList className="grid grid-cols-4 gap-1 p-1 w-full bg-background/50 border border-border/20">
          <TabsTrigger 
            value="beginner" 
            className="flex items-center gap-1.5 data-[state=active]:bg-gradient-to-r data-[state=active]:from-purple-500 data-[state=active]:to-indigo-500 data-[state=active]:text-white transition-all duration-300"
          >
            <Sparkles className="h-4 w-4" />
            <span>Beginners ({faqs.beginner?.length || 0})</span>
          </TabsTrigger>
          <TabsTrigger 
            value="intermediate"
            className="flex items-center gap-1.5 data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500 data-[state=active]:to-cyan-500 data-[state=active]:text-white transition-all duration-300"
          >
            <GraduationCap className="h-4 w-4" />
            <span>Intermediate ({faqs.intermediate?.length || 0})</span>
          </TabsTrigger>
          <TabsTrigger 
            value="expert"
            className="flex items-center gap-1.5 data-[state=active]:bg-gradient-to-r data-[state=active]:from-teal-500 data-[state=active]:to-emerald-500 data-[state=active]:text-white transition-all duration-300"
          >
            <GraduationCap className="h-4 w-4 font-bold" />
            <span>Expert ({faqs.expert?.length || 0})</span>
          </TabsTrigger>
          <TabsTrigger 
            value="company"
            className="flex items-center gap-1.5 data-[state=active]:bg-gradient-to-r data-[state=active]:from-amber-500 data-[state=active]:to-orange-500 data-[state=active]:text-white transition-all duration-300"
          >
            <Building className="h-4 w-4" />
            <span>Company ({faqs.company?.length || 0})</span>
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="beginner" className="space-y-4 animate-in fade-in-50 duration-300">
          {faqs.beginner?.map((faq, index) => (
            <FaqItem key={`beginner-${index}`} faq={faq} category="beginner" />
          ))}
        </TabsContent>
        
        <TabsContent value="intermediate" className="space-y-4 animate-in fade-in-50 duration-300">
          {faqs.intermediate?.map((faq, index) => (
            <FaqItem key={`intermediate-${index}`} faq={faq} category="intermediate" />
          ))}
        </TabsContent>
        
        <TabsContent value="expert" className="space-y-4 animate-in fade-in-50 duration-300">
          {faqs.expert?.map((faq, index) => (
            <FaqItem key={`expert-${index}`} faq={faq} category="expert" />
          ))}
        </TabsContent>
        
        <TabsContent value="company" className="space-y-4 animate-in fade-in-50 duration-300">
          {faqs.company?.map((faq, index) => (
            <FaqItem key={`company-${index}`} faq={faq} category="company" />
          ))}
        </TabsContent>
      </Tabs>
      
      <div className="flex justify-between items-center border-t border-border/20 pt-4">
        <Button 
          onClick={onGenerateNew} 
          variant="outline" 
          className="flex items-center gap-2 bg-gradient-to-r from-purple-500/10 to-indigo-500/10 hover:from-purple-500/20 hover:to-indigo-500/20 hover:text-purple-400 transition-all duration-300 border-purple-500/20"
        >
          <RefreshCw className="h-4 w-4" />
          <span>New Analysis</span>
        </Button>
        
        <Button
          onClick={copyAllFaqs}
          variant="outline"
          className="flex items-center gap-2 bg-gradient-to-r from-amber-500/10 to-orange-500/10 hover:from-amber-500/20 hover:to-orange-500/20 hover:text-amber-400 transition-all duration-300 border-amber-500/20"
        >
          <Copy className="h-4 w-4" />
          <span>Copy All FAQs</span>
        </Button>
      </div>
    </div>
  );
};

export default FaqTabs;
